import { memo, useEffect } from 'react'
import { useIntl } from 'react-intl'
import clsx from 'clsx'
import styled from 'styled-components'
import { rem } from 'polished'
import IconButton from 'LuxKit/Button/IconButton'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import noop from 'lib/function/noop'
import ChatMapOfferCard from './ChatMapOfferCard'
import ChatMapOffersDrawerSkeleton from './ChatMapOffersDrawerSkeleton'
import type { ExploreOffer } from 'tripPlanner/api/explore/types'
import type { TripItineraryData } from 'tripPlanner/api/types'
import productLabels from 'i18n/productLabels'

const TRANSITION_DURATION = 300

export const OFFERS_DRAWER_WIDTH = 380

const DrawerWrapper = styled.div`
  flex-shrink: 0;
  width: 0;

  &.open {
    width: ${rem(OFFERS_DRAWER_WIDTH)};
  }
`

const Panel = styled.div`
  width: ${rem(OFFERS_DRAWER_WIDTH)};
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.palette.neutral.default.eight};
  border-left: 1px solid ${props => props.theme.palette.neutral.default.six};
  transform: translateX(100%);
  will-change: transform;
  transition: transform ${TRANSITION_DURATION}ms ease-out;

  .open > & {
    transform: translateX(0);
  }
`

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: ${rem(16)} ${rem(20)};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.six};
  flex-shrink: 0;
  gap: ${rem(16)};
`

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
  flex: 1;
  min-width: 0;
`

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
`

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
  padding: ${rem(16)};
`

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${rem(48)} ${rem(24)};
  text-align: center;
`

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${rem(48)} ${rem(24)};
  text-align: center;
  gap: ${rem(16)};
`

interface Props {
  isOpen: boolean
  onClose: () => void
  offers: Array<ExploreOffer>
  offerType: 'hotel' | 'experience'
  isLoading: boolean
  isError: boolean
  onRetry: () => void
  itineraryData?: TripItineraryData
  onSendMessage: (message: string) => void
  onViewOffer: (data: { offerId: string; offerType?: string }) => void
  highlightedMarkerId?: string
  onCardHover?: (offerId: string | undefined) => void
}

function ChatMapOffersDrawer({
  isOpen,
  onClose,
  offers,
  offerType,
  isLoading,
  isError,
  onRetry,
  itineraryData,
  onSendMessage,
  onViewOffer,
  highlightedMarkerId,
  onCardHover = noop,
}: Props) {
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  const intl = useIntl()

  const offersHeading = offerType === 'experience'
    ? intl.formatMessage({
        id: 'mdah2gC105p2yHp6epsS',
        defaultMessage: 'Experiences',
        description: 'Section heading for the map offers drawer listing experiences',
      })
    : intl.formatMessage(productLabels.hotels)

  const closePanelLabel = intl.formatMessage({
    id: 'ksE-HAW1PsIC7L4s6cLD',
    defaultMessage: 'Close panel',
    description: 'Aria-label for the close button on the map offers drawer',
  })

  const errorMessage = offerType === 'experience'
    ? intl.formatMessage({
        id: 'PystxXcZkTlt6NK4ILqq',
        defaultMessage: 'Something went wrong loading experiences.',
        description: 'Error message shown when experience offers fail to load in the map drawer',
      })
    : intl.formatMessage({
        id: '9xNuYzIT33aItyjX_RDp',
        defaultMessage: 'Something went wrong loading hotels.',
        description: 'Error message shown when hotel offers fail to load in the map drawer',
      })

  const tryAgainLabel = intl.formatMessage({
    id: '9kVgfxm5hxnqwVoYaC-I',
    defaultMessage: 'Try again',
    description: 'Button label to retry loading hotel offers after an error',
  })

  const resultsCountLabel = intl.formatMessage(
    {
      id: '3-GCnO1r8Qlb4W2MVOlO',
      defaultMessage: '{count, plural, one {# result} other {# results}}',
      description: 'Results count label in the map offers drawer header',
    },
    { count: offers.length },
  )

  const emptyStateMessage = offerType === 'experience'
    ? intl.formatMessage({
        id: 'aDJHGhRheQ-wfqFZqEBb',
        defaultMessage: 'No experiences found in this area. Try panning or zooming the map.',
        description: 'Empty state message when no experience offers are found in the current map area',
      })
    : intl.formatMessage({
        id: 'gzI1eSJXgCbhpntmoK41',
        defaultMessage: 'No hotels found in this area. Try panning or zooming the map.',
        description: 'Empty state message when no hotel offers are found in the current map area',
      })

  return (
    <DrawerWrapper className={clsx({ open: isOpen })} aria-hidden={!isOpen}>
      <Panel>
        <Header>
          <HeaderContent>
            <Heading variant="heading5">{offersHeading}</Heading>
            {!isLoading && !isError && (
              <BodyText variant="small" colour="neutral-three">
                {resultsCountLabel}
              </BodyText>
            )}
          </HeaderContent>
          <IconButton
            kind="tertiary"
            variant="dark"
            size="medium"
            onClick={onClose}
            aria-label={closePanelLabel}
          >
            <LineTimesIcon />
          </IconButton>
        </Header>
        <Body>
          {isLoading && <ChatMapOffersDrawerSkeleton />}
          {!isLoading && isError && (
            <ErrorState>
              <Group direction="vertical" gap={8} horizontalAlign="center">
                <BodyText variant="medium" colour="neutral-two">
                  {errorMessage}
                </BodyText>
                <TextButton
                  kind="secondary"
                  size="small"
                  onClick={onRetry}
                >
                  {tryAgainLabel}
                </TextButton>
              </Group>
            </ErrorState>
          )}
          {!isLoading && !isError && offers.length === 0 && (
            <EmptyState>
              <BodyText variant="medium" colour="neutral-three">
                {emptyStateMessage}
              </BodyText>
            </EmptyState>
          )}
          {!isLoading && !isError && offers.length > 0 && (
            <CardList>
              {offers.map((offer) => (
                <ChatMapOfferCard
                  key={offer.id}
                  offer={offer}
                  itineraryData={itineraryData}
                  onSendMessage={onSendMessage}
                  onViewOffer={onViewOffer}
                  onHover={onCardHover}
                  isHighlighted={highlightedMarkerId === offer.id}
                />
              ))}
            </CardList>
          )}
        </Body>
      </Panel>
    </DrawerWrapper>
  )
}

export default memo(ChatMapOffersDrawer)
